import { ref, computed } from 'vue'
import { useUiStore } from '@/stores/ui'

// ============================================================
// useActiveScreen — Resolvedor de Tela Ativa
// ============================================================
//
// Problema:
//   activeCadastroComponent no MainDashboard resolve para o container
//   (ex: EmbarquesCadastro), mas o formulário real sendo exibido é
//   outro (ex: EmbarquesForm, EmbarquesMateriaisForm, etc.).
//   Isso faz o RAG carregar a documentação errada.
//
// Solução:
//   Composable singleton com estado reativo compartilhado.
//   Os orquestradores (*Cadastro.vue que envolvem *Form.vue)
//   chamam setActiveForm() no setup, e qualquer consumidor
//   (MainDashboard, RAG, etc.) consulta activeForm.
//
// Uso:
//   const { activeForm, setActiveForm } = useActiveScreen()
//   setActiveForm('EmbarquesForm')
//
//   Em outro componente:
//   const { activeForm } = useActiveScreen()
//   console.log(activeForm.value) // 'EmbarquesForm'
// ============================================================

// Registro: módulos cujo *Cadastro é um orquestrador, não o form real
const ORCHESTRATED_MODULES = {
  PurchaseOrderAdministrar: {
    defaultForm: 'PurchaseOrderForm',
    hasDynamicForm: false
  },
  EmbarquesAdministrar: {
    defaultForm: 'EmbarquesForm',
    hasDynamicForm: true
  },
  ContratoCompraAdministrar: {
    defaultForm: 'ContratoCompraForm',
    hasDynamicForm: false
  }
}

// Estado reativo compartilhado (fora da função = singleton)
const activeFormName = ref(null)
const activeContainerName = ref(null)

export function useActiveScreen () {
  const uiStore = useUiStore()

  const currentModule = computed(() => uiStore.currentModule)

  /** Nome do container/orquestrador (convenção Administrar → Cadastro) */
  const containerName = computed(() => {
    if (!currentModule.value) return null
    return currentModule.value.replace('Administrar', 'Cadastro')
  })

  /** Nome do formulário REAL que o usuário está vendo */
  const activeForm = computed(() => {
    // 1. Se o orquestrador registrou um form dinâmico, usa ele
    if (activeFormName.value) return activeFormName.value

    // 2. Se é um módulo orquestrado conhecido, retorna o default
    const registry = ORCHESTRATED_MODULES[currentModule.value]
    if (registry) return registry.defaultForm

    // 3. Padrão: o container É o formulário (módulos normais)
    return containerName.value
  })

  /** Chamado pelo orquestrador para registrar o form ativo */
  function setActiveForm (name) {
    activeFormName.value = name
  }

  /** Chamado pelo orquestrador para registrar o container ativo */
  function setActiveContainer (name) {
    activeContainerName.value = name
  }

  /** Se o módulo atual tem formulário dinâmico (ex: Embarques) */
  const isDynamicForm = computed(() =>
    ORCHESTRATED_MODULES[currentModule.value]?.hasDynamicForm ?? false
  )

  return {
    containerName,
    activeForm,
    setActiveForm,
    setActiveContainer,
    isDynamicForm
  }
}
